import CircularProgress from "@mui/material/CircularProgress";
import { useAddToCart } from "../../../../../hooks/useAddToCart";
import useWeighingItemModal from "../../../../../hooks/useWeighingItemModal";
import { UserContext } from "App";
import { useContext, useState } from "react";
import Swal from "sweetalert2";
import {
  deleteItemThroughScanner,
  editCart,
} from "../../../../../../../../config/Services";
import { useSnackbar } from "../../../../../hooks/SnackbarProvider";
import { getFromLocalStorage } from "../../../../../../../../utils/localStorageHelpers";
import BatchSelectionModal from "../batch-popup/index";
import CartItemDrawer from "../../../cartItemTable/Drawer/CartItemDrawer/CartItemDrawer";
import { getStoreCurrencySymbol } from "../../../../../../../../utils/currency";
import "./ProductGrid.css";

const newSwal = Swal.mixin({
  customClass: {
    confirmButton: "button button-success",
    cancelButton:  "button button-error",
    container:     "my-swal-container",
  },
  buttonsStyling: false,
});

const AVATAR_COLORS = ["#2a71ff","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#f97316"];

/** Extract a plain number from a price field that may be {mrp, currency} or a number */
function extractPrice(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return val;
  if (typeof val === "object") return val?.mrp ?? val?.price ?? val?.value ?? null;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? null : parsed;
}

/** Normalise a product from either getPopularProducts or FilterProduct flattenResponse */
function normalise(product) {
  const name =
    product?.variant?.name || product?.name || product?.productName || "";

  const mrp = extractPrice(
    product?.variant?.inventorySync?.mrp ?? product?.mrp
  );

  const sp = extractPrice(
    product?.variant?.inventorySync?.sellingPrice ?? product?.sellingPrice
  );

  const gtin =
    product?.gtin ||
    product?.variant?.barcodes?.[0] ||
    product?.bundleBarcode ||
    null;

  // Try every known image field in the API response
  const images = product?.variant?.images;
  const rawImg =
    (Array.isArray(images) ? images[0] : null) ||
    images?.front ||
    images?.back  ||
    images?.left  ||
    images?.right ||
    images?.main  ||
    product?.imageUrl ||
    product?.image ||
    null;

  return { name, mrp, sp, gtin, rawImg };
}

/** Default product image — shown when the API returns no image */
function DefaultProductImg({ avatarBg }) {
  return (
    <div className="pg-card-img-wrap pg-card-img-wrap--default" style={{ background: avatarBg }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="rgba(255,255,255,0.85)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.29 7 12 12 20.71 7"/>
        <line x1="12" y1="22" x2="12" y2="12"/>
      </svg>
    </div>
  );
}

export default function ProductGrid({ products = [], title, emptyText = "No products found", closeModal }) {
  const showSnackbar = useSnackbar();
  const { addItemWithCheck, addItemDirect, applyCartState } = useAddToCart();
  const [addingGtin,  setAddingGtin]  = useState(null);
  const { findWeighingItem, renderWeighingModal } = useWeighingItemModal({
    setAddItemThroughParticularCategoryItemLoader: setAddingGtin,
  });
  const {
    cartItems,
    setCartItems, setCartTotalQty,
    setCgst, setSgst, setCess,
    setTotal, setSubTotal,
    setanimationBox, setItemgtin,
    setHighlightMrp, setSearchText,
  } = useContext(UserContext);

  const [cardStatus,  setCardStatus]  = useState({});  // { [gtin]: { ok: bool, msg: string } }

  /* Flash a short status message on the card button, then clear it */
  const flashStatus = (gtin, ok, msg) => {
    setCardStatus((prev) => ({ ...prev, [gtin]: { ok, msg } }));
    setTimeout(
      () => setCardStatus((prev) => { const next = { ...prev }; delete next[gtin]; return next; }),
      ok ? 2200 : 3500
    );
  };

  /* ── Batch selection modal state ── */
  const [batchModalOpen,    setBatchModalOpen]    = useState(false);
  const [batchMrps,         setBatchMrps]         = useState([]);
  const [batchProductName,  setBatchProductName]  = useState("");
  const [batchGtin,         setBatchGtin]         = useState("");
  const [selectedMrp,       setSelectedMrp]       = useState("");
  const [selectedSP,        setSelectedSP]        = useState("");
  const [batchNo,           setBatchNo]           = useState("");
  const [purchasePrice,     setPurchasePrice]     = useState("");
  const [sellingUnitState,  setSellingUnitState]  = useState("");
  const [offerData,         setOfferData]         = useState(null);
  const [availableUnits,    setAvailableUnits]    = useState("");
  const [expiryDate,        setExpiryDate]        = useState("");
  const [batchDisabled,     setBatchDisabled]     = useState(true);
  const [detailProduct,    setDetailProduct]    = useState(null); // product info drawer

  const locId  = localStorage.getItem("locId");
  const orgId  = localStorage.getItem("orgId");

  /* ── Add a product to cart ── */
  const handleAdd = async (product) => {
    const CartId = localStorage.getItem("CartId");
    const { gtin, name, mrp, sp, rawImg } = normalise(product);
    const avatarBg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    if (!gtin) { showSnackbar("Product has no barcode", "error"); return; }

    /* Weighing items: open the quantity modal instead of adding directly */
    const isWeighable = product?.variant?.needsWeighingScaleIntegration === true;
    if (isWeighable) setAddingGtin(gtin); // spinner until the modal closes or the add settles
    if (
      isWeighable &&
      findWeighingItem(
        gtin,
        name,
        "weighingScale",
        product?.variant?.weightUnit || product?.variant?.sellingUnit
      )
    ) {
      return;
    }

    const allowNegativeSelling =
      !!getFromLocalStorage("appConfig")?.enabledFeatures?.NEGATIVE_INVENTORY;

    setAddingGtin(gtin);

    await addItemWithCheck(
      { gtin, cartId: CartId, locationId: locId, orgId, allowNegativeSelling },
      {
        /* ── Clear spinner before any error dialog ── */
        onBeforeError: () => setAddingGtin(null),

        /* ── Direct add: cart updated immediately ── */
        onSuccess: (cartData) => {
          setAddingGtin(null);
          setanimationBox(true);
          setItemgtin(gtin);
          setHighlightMrp(cartData?.cartProducts?.[0]?.sellingPrice);
          flashStatus(gtin, true, "Added to cart");
        },

        /* ── API error (es=1/2, non-stale, non-no-cart) — most likely never inwarded ── */
        onError: async (errMsg) => {
          await newSwal.fire({
            title: "",
            html:
              `<div style="text-align:center;padding:4px 0 16px;">` +
                `<div style="width:60px;height:60px;border-radius:50%;background:#fef2f2;display:flex;align-items:center;justify-content:center;margin:0 auto 12px;border:2px solid #fecaca;">` +
                  `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">` +
                    `<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>` +
                    `<line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>` +
                  `</svg>` +
                `</div>` +
                `<p style="font-size:17px;font-weight:700;color:#0f172a;margin:0 0 4px;">No Inventory Found</p>` +
                `<p style="font-size:12.5px;color:#64748b;margin:0 0 16px;line-height:1.5;">${name || gtin}</p>` +
              `</div>` +
              `<div style="background:#fef2f2;border:1.5px solid #fecaca;border-radius:12px;padding:14px 16px;text-align:left;">` +
                `<p style="margin:0 0 6px;font-size:13px;font-weight:700;color:#991b1b;">Cannot add to cart</p>` +
                `<p style="margin:0;font-size:12.5px;color:#7f1d1d;line-height:1.65;">` +
                  `This product has <strong>no inward records</strong>. Please complete a GRN (goods receipt note) in Pallet ROS before this product can be sold at the POS.` +
                `</p>` +
              `</div>`,
            showCancelButton: false,
            confirmButtonText: "Got it",
            width: "460px",
            willOpen: (popup) => {
              /* Keep the popup above the MUI search modal (z-index 1300) */
              const container = popup.closest(".swal2-container");
              if (container) container.style.zIndex = "99999";
            },
            didOpen: (popup) => {
              const confirmBtn = popup.querySelector(".swal2-confirm");
              if (confirmBtn) confirmBtn.style.cssText =
                "background:#2a71ff;color:#fff;border:none;padding:10px 28px;" +
                "border-radius:8px;font-weight:700;font-size:13.5px;cursor:pointer;min-width:120px;margin-top:16px;";
            },
          });
        },

        /* ── Batch selection: 1 batch → auto-add, >1 → show picker ── */
        onBatchSelect: (dataArr) => {
          setAddingGtin(null);

          if (dataArr.length === 1) {
            /* Single batch — add directly without showing the picker */
            const item = dataArr[0];
            setAddingGtin(gtin);
            addItemDirect(
              {
                cartId:           CartId,
                gtin:             item.gtin || gtin,
                locationId:       locId,
                orgId,
                mrp:              item.mrp,
                sellingPrice:     item.sellingPrice,
                inventoryChecks:  "NO",
                isOfferAvailable: false,
                batchNo:          item.batchNo || item.batchNum || "",
                purchasePrice:    item.purchasePrice || "",
                sellingUnit:      item.sellingUnit   || "",
              },
              cartItems,
              {
                onBeforeError: () => setAddingGtin(null),
                onSuccess: (cartData) => {
                  setAddingGtin(null);
                  setanimationBox(true);
                  setItemgtin(gtin);
                  setHighlightMrp(cartData?.cartProducts?.[0]?.sellingPrice);
                  flashStatus(gtin, true, "Added to cart");
                },
              }
            );
          } else {
            /* Multiple batches — open the batch picker */
            const batchData = dataArr.map((item) => ({
              mrp:            item.mrp,
              sellingPrice:   item.sellingPrice,
              batch:          item.batchNo || item.batchNum,
              batchNo:        item.batchNo || item.batchNum,
              batchNum:       item.batchNo || item.batchNum,
              quantity:       item.availableUnits,
              offers:         item.offers,
              purchasePrice:  item.purchasePrice,
              isMultiple:     item.isMultiple,
              availableUnits: item.availableUnits,
              expiryDate:     item.expiryDate,
              sellingUnit:    item.sellingUnit,
              gtin:           item.gtin,
            }));
            setBatchMrps(batchData);
            setBatchProductName(name);
            setBatchGtin(gtin);
            setBatchDisabled(true);
            setSelectedMrp(""); setSelectedSP(""); setBatchNo("");
            setPurchasePrice(""); setSellingUnitState(""); setOfferData(null);
            setAvailableUnits(""); setExpiryDate("");
            setBatchModalOpen(true);
          }
        },

        /* ── Oversell: inwarded product with no current stock ── */
        onOversell: async (preAddRes) => {
          setAddingGtin(null);

          /*
           * newCartItemsAddingProducts may have already placed the item in the
           * cart before returning an empty array.  Roll that back immediately.
           *   Case (a) item was NOT previously in cart → delete the new row.
           *   Case (b) item WAS in cart → qty was incremented; restore it.
           */
          const preAddCartData = preAddRes?.data?.data?.data || preAddRes?.data?.data;
          const justAdded      = preAddCartData?.cartProducts?.find((p) => p.gtin === gtin);
          const prevItem       = cartItems.find((ci) => ci.gtin === gtin);

          if (justAdded?.cartProductId) {
            try {
              if (!prevItem) {
                await deleteItemThroughScanner({
                  cartId:          CartId,
                  cartProductId:   justAdded.cartProductId,
                  gtin:            justAdded.gtin,
                  locationId:      locId,
                  mrp:             justAdded.mrp           || mrp || "",
                  sellingPrice:    justAdded.sellingPrice  || sp  || "",
                  inventoryChecks: "NO",
                  batchNo:         justAdded.batchNo       || "",
                  purchasePrice:   justAdded.purchasePrice || "",
                  quantity:        justAdded.quantity,
                  quantityBySpecs: justAdded.quantityBySpecs || null,
                  sellingUnit:     justAdded.sellingUnit   || "",
                });
              } else if (justAdded.quantity > prevItem.quantity) {
                await editCart({
                  cartId:          CartId,
                  cartProductId:   justAdded.cartProductId,
                  qty:             prevItem.quantity,
                  quantityBySpecs: null,
                  updatedBy:       "",
                  sellingPrice:    justAdded.sellingPrice || sp || mrp || "",
                });
              }
            } catch (_) {
              /* Silent — worst case the cart has an extra unit until next refresh */
            }
          }

          /* ── Image HTML ── */
          const imgHtml = rawImg
            ? `<img src="${rawImg}" alt="" style="width:60px;height:60px;object-fit:cover;border-radius:10px;display:block;flex-shrink:0;" />`
            : `<div style="width:60px;height:60px;border-radius:10px;background:${avatarBg};display:flex;align-items:center;justify-content:center;flex-shrink:0;">` +
                `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">` +
                  `<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>` +
                  `<polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/>` +
                `</svg>` +
              `</div>`;

          /* ── Oversell confirmation Swal ── */
          const result = await newSwal.fire({
            title: "",
            html:
              `<div style="background:#f8fafc;border-radius:12px;padding:14px 16px;display:flex;align-items:center;gap:14px;margin-bottom:14px;border:1.5px solid #e2e8f0;text-align:left;">` +
                imgHtml +
                `<div style="flex:1;min-width:0;">` +
                  `<p style="margin:0 0 3px;font-size:14px;font-weight:700;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name || "Product"}</p>` +
                  `<p style="margin:0 0 7px;font-size:11.5px;font-family:'Courier New',monospace;color:#64748b;letter-spacing:0.06em;">${gtin}</p>` +
                  `<span style="display:inline-flex;align-items:center;gap:5px;font-size:12.5px;font-weight:700;color:#b45309;background:#fef3c7;border-radius:999px;padding:3px 11px;">` +
                    `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>` +
                    `No inventory` +
                  `</span>` +
                `</div>` +
              `</div>` +
              `<div style="margin-bottom:14px;">` +
                `<p style="margin:0 0 10px;font-size:12px;font-weight:600;color:#475569;text-align:left;">Quantity</p>` +
                `<div style="display:flex;align-items:center;gap:0;border:1.5px solid #e2e8f0;border-radius:10px;overflow:hidden;width:160px;">` +
                  `<button id="swal-qty-minus" type="button" style="width:44px;height:44px;background:#f1f5f9;border:none;font-size:22px;font-weight:400;cursor:pointer;color:#475569;line-height:1;padding:0;">−</button>` +
                  `<input id="swal-qty-input" type="number" value="1" min="1" max="999" style="flex:1;height:44px;border:none;border-left:1.5px solid #e2e8f0;border-right:1.5px solid #e2e8f0;text-align:center;font-size:16px;font-weight:700;color:#0f172a;outline:none;-moz-appearance:textfield;" />` +
                  `<button id="swal-qty-plus"  type="button" style="width:44px;height:44px;background:#f1f5f9;border:none;font-size:22px;font-weight:400;cursor:pointer;color:#475569;line-height:1;padding:0;">+</button>` +
                `</div>` +
              `</div>` +
              `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:12px 14px;text-align:left;">` +
                `<p style="margin:0;font-size:13px;color:#78350f;line-height:1.65;">` +
                  `Selling at the <strong>last inwarded batch price</strong> — tagged as <strong>oversold inventory</strong>.` +
                `</p>` +
              `</div>`,
            showCancelButton: true,
            confirmButtonText: "Add to cart",
            cancelButtonText:  "Cancel",
            reverseButtons: true,
            width: "500px",
            preConfirm: () => {
              const input = document.getElementById("swal-qty-input");
              const qty = parseInt(input?.value || "1", 10);
              if (!qty || qty < 1) {
                Swal.showValidationMessage("Please enter a valid quantity (minimum 1)");
                return false;
              }
              return qty;
            },
            didOpen: (popup) => {
              const confirmBtn = popup.querySelector(".swal2-confirm");
              const cancelBtn  = popup.querySelector(".swal2-cancel");
              if (confirmBtn) confirmBtn.style.cssText =
                "background:#2a71ff;color:#fff;border:none;padding:10px 26px;" +
                "border-radius:8px;font-weight:700;font-size:13.5px;cursor:pointer;min-width:130px;";
              if (cancelBtn) cancelBtn.style.cssText =
                "background:transparent;color:#2a71ff;border:2px solid #2a71ff;" +
                "padding:9px 26px;border-radius:8px;font-weight:700;font-size:13.5px;" +
                "cursor:pointer;min-width:100px;";
              const minus = document.getElementById("swal-qty-minus");
              const plus  = document.getElementById("swal-qty-plus");
              const inp   = document.getElementById("swal-qty-input");
              minus?.addEventListener("click", () => {
                const v = parseInt(inp.value || "1", 10);
                if (v > 1) inp.value = v - 1;
              });
              plus?.addEventListener("click", () => {
                const v = parseInt(inp.value || "1", 10);
                inp.value = v + 1;
              });
              const style = document.createElement("style");
              style.textContent = "#swal-qty-input::-webkit-inner-spin-button,#swal-qty-input::-webkit-outer-spin-button{-webkit-appearance:none;margin:0;}";
              popup.appendChild(style);
            },
          });

          /* User cancelled — cart already clean from rollback above */
          if (!result.isConfirmed) return;

          const qtyToAdd = result.value || 1;
          setAddingGtin(gtin);

          /*
           * Confirmed oversell add via addItemDirect.
           * Empty snapshot → addToCartDedup always calls AddItemThroughScanner
           * (avoids stale cartProductId after the rollback above).
           */
          await addItemDirect(
            {
              cartId:           CartId,
              gtin,
              locationId:       locId,
              orgId,
              mrp:              mrp || "",
              sellingPrice:     sp  || "",
              inventoryChecks:  "NO",
              isOfferAvailable: false,
              batchNo:          "",
              purchasePrice:    "",
              sellingUnit:      "",
            },
            [],
            {
              onBeforeError: () => {
                setAddingGtin(null);
                flashStatus(gtin, false, "Failed");
              },
              onSuccess: async (cartData) => {
                if (qtyToAdd > 1) {
                  const addedItem = cartData?.cartProducts?.find((p) => p.gtin === gtin);
                  if (addedItem?.cartProductId) {
                    try {
                      const editRes = await editCart({
                        cartId:          CartId,
                        cartProductId:   addedItem.cartProductId,
                        qty:             (addedItem.quantity || 1) + (qtyToAdd - 1),
                        quantityBySpecs: null,
                        updatedBy:       "",
                        sellingPrice:    addedItem.sellingPrice || sp || mrp || "",
                      });
                      const editData = editRes?.data?.data?.data || editRes?.data?.data;
                      applyCartState(editData);
                      setHighlightMrp(editData?.cartProducts?.[0]?.sellingPrice);
                    } catch (_) {
                      setHighlightMrp(cartData?.cartProducts?.[0]?.sellingPrice);
                    }
                  } else {
                    setHighlightMrp(cartData?.cartProducts?.[0]?.sellingPrice);
                  }
                } else {
                  setHighlightMrp(cartData?.cartProducts?.[0]?.sellingPrice);
                }
                setAddingGtin(null);
                setanimationBox(true);
                setItemgtin(gtin);
                flashStatus(gtin, true, "Added to cart");
              },
            }
          );
        },
      }
    );
  };

  /* ── Batch modal helpers ── */
  const handlePrice = (mrp, sp, bno, pp, su, offers, avail, expiry) => {
    setSelectedMrp(mrp   || "");
    setSelectedSP(sp     || "");
    setBatchNo(bno       || "");
    setPurchasePrice(pp  || "");
    setSellingUnitState(su || "");
    setOfferData(offers  || null);
    setAvailableUnits(avail  || "");
    setExpiryDate(expiry     || "");
    setBatchDisabled(!mrp);
  };

  const addItemFromBatch = ({ gtin: itemGtin, cartId }, mrp, sp, bno, pp, su) => {
    // Error handling (stale cart, no cart, generic) delegated to useAddToCart hook
    return addItemDirect({
      cartId,
      gtin:            itemGtin,
      locationId:      locId,
      orgId,
      mrp,
      sellingPrice:    sp,
      inventoryChecks: "NO",
      isOfferAvailable: false,
      batchNo:         bno,
      purchasePrice:   pp,
      sellingUnit:     su,
    }, cartItems, {
      onSuccess: (cartData) => {
        setBatchModalOpen(false);
        // Core cart state (cartItems, billing, offerFound) applied by useAddToCart hook
        setanimationBox(true);
        setItemgtin(itemGtin);
        setHighlightMrp(cartData?.cartProducts?.[0]?.sellingPrice);
        flashStatus(itemGtin, true, "Added to cart");
      },
    });
  };

  if (!products.length) {
    return (
      <div className="pg-empty-state">
        <div className="pg-empty-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true">
            <path d="M5 8h14" /><path d="M5 12h9" /><path d="M5 16h5" />
            <rect x="2" y="4" width="20" height="17" rx="2" />
            <circle cx="19" cy="18" r="3" fill="none" />
            <path d="m21.5 20.5-1.5-1.5" />
          </svg>
        </div>
        <p className="pg-empty-heading">No products found</p>
        <p className="pg-empty-sub">{emptyText}</p>
      </div>
    );
  }

  return (
    <>
    <div className="pg-wrapper">
      {title && <p className="pg-section-title">{title}</p>}
      <div className="pg-grid">
        {products.map((product, i) => {
          const { name, mrp, sp, gtin, rawImg } = normalise(product);
          const avatarBg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
          const isAdding = addingGtin === gtin;
          const status   = cardStatus[gtin] ?? null;

          /*
           * isOutOfStockEligible – inwarded before but no current stock → can oversell.
           * "No inward done" is not checked here — inventory checks are handled by the backend.
           *
           * Two response shapes are supported:
           *   Nested  (flattenResponse omitted) → product.variant.inventorySync.*
           *   Flat    (flattenResponse: true)   → product.inventoryId / product.availableUnits
           */
          const inventorySync = product?.variant?.inventorySync;

          let isOutOfStockEligible;
          if (inventorySync !== undefined) {
            // Nested shape — inventorySync contains inward data
            const hasNoInventoryId = !inventorySync?.inventoryId;
            const hasGrnPrice      = !!(inventorySync?.mrp || inventorySync?.sellingPrice);
            isOutOfStockEligible = hasNoInventoryId && hasGrnPrice;
          } else {
            // Flat shape (flattenResponse: true) — inventoryId is the inward indicator
            const invId      = product?.inventoryId;
            isOutOfStockEligible = !!invId && !product?.availableUnits;
          }

          /* Price logic: strikethrough MRP only when SP exists and is different */
          const mrpNum = mrp != null ? parseFloat(mrp) : null;
          const spNum  = sp  != null ? parseFloat(sp)  : null;
          const hasDifferentSP = spNum !== null && mrpNum !== null && spNum !== mrpNum;

          /* ── Product type indicators ── */
          /*
           * Only flag as weighable when the product is explicitly configured to
           * require a weighing-scale integration.  Using weightUnit or sellingUnit
           * alone produced false positives on almost every product because many
           * catalogue entries have weightUnit set even for non-scale items.
           */
          const isWeighable = product?.variant?.needsWeighingScaleIntegration === true;

          const isBundle = !!(product?.isBundle || product?.productType === "BUNDLE" || product?.bundleBarcode);

          const batchCount    = parseInt(product?.batchCount || product?.variant?.batchCount || 0, 10);
          const hasMultiBatch = batchCount > 1
            || product?.variant?.multipleBatches === true
            || !!(product?.variant?.trackBatch)
            || !!(product?.hasMultipleBatches);

          return (
            <div key={gtin || i} className="pg-card">
              {/* Clickable area (image + body) — opens product detail drawer */}
              <div
                className="pg-card-clickable"
                role="button"
                tabIndex={0}
                title="View product details"
                onClick={() => gtin && setDetailProduct({ gtin, productName: name, productImage: rawImg })}
                onKeyDown={(e) => e.key === "Enter" && gtin && setDetailProduct({ gtin, productName: name, productImage: rawImg })}
              >
              {/* Product image (API) or default product icon */}
              <div className="pg-card-img-container">
                {rawImg ? (
                  <div className="pg-card-img-wrap">
                    <img src={rawImg} alt={name} className="pg-card-img"
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("pg-card-img-wrap--default"); e.currentTarget.parentElement.style.background = avatarBg; }}
                    />
                  </div>
                ) : (
                  <DefaultProductImg avatarBg={avatarBg} />
                )}

                {/* Type badges overlay — top-right of image */}
                {(isWeighable || isBundle || hasMultiBatch) && (
                  <div className="pg-card-badges">
                    {isWeighable && (
                      <span className="pg-card-badge pg-card-badge--scale" title="Sold by weight">
                        {/* Weighing scale icon */}
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M12 3a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/>
                          <path d="M6.5 8C6.5 8 5 14 2 14s-3-6-3-6"/>
                          <path d="M21.5 8C21.5 8 23 14 26 14s3-6 3-6"/>
                          <path d="M2 14h20"/>
                          <path d="M3 8h18"/>
                          <path d="M12 2v6"/>
                          <path d="M3 8c0-2.2 4-4 9-4s9 1.8 9 4"/>
                          <path d="M4 22h16"/>
                          <path d="M12 14v8"/>
                        </svg>
                        <span>Scale</span>
                      </span>
                    )}
                    {isBundle && (
                      <span className="pg-card-badge pg-card-badge--bundle" title="Bundle product">
                        {/* Boxes/bundle icon */}
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z"/>
                          <path d="m7 16.5-4.74-2.85"/><path d="m7 16.5 5-3"/>
                          <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z"/>
                          <path d="m17 16.5-5-3"/><path d="m17 16.5 4.74-2.85"/>
                          <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z"/>
                          <path d="M12 8 7.26 5.15"/><path d="m12 8 4.74-2.85"/>
                        </svg>
                        <span>Bundle</span>
                      </span>
                    )}
                    {hasMultiBatch && (
                      <span className="pg-card-badge pg-card-badge--batch" title={batchCount > 0 ? `${batchCount} batches` : "Multiple batches"}>
                        {/* Layers/batch icon */}
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                          <polyline points="2 17 12 22 22 17"/>
                          <polyline points="2 12 12 17 22 12"/>
                        </svg>
                        <span>{batchCount > 0 ? batchCount : ""} {batchCount > 0 ? "Batches" : "Batch"}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Card body */}
              <div className="pg-card-body">
                {/* Title */}
                <p className="pg-card-name" title={name}>{name || "—"}</p>

                {/* Barcode */}
                {gtin && (
                  <p className="pg-card-barcode">{gtin}</p>
                )}

                {/* Prices */}
                <div className="pg-card-prices">
                  {mrpNum !== null && (
                    hasDifferentSP ? (
                      <span className="pg-card-mrp">{getStoreCurrencySymbol()}{mrp}</span>
                    ) : (
                      <span className="pg-card-price-single">{getStoreCurrencySymbol()}{mrp}</span>
                    )
                  )}
                  {hasDifferentSP && (
                    <span className="pg-card-sp">{getStoreCurrencySymbol()}{sp}</span>
                  )}
                </div>

                {/* Multiple-batch badge — visible in the card body, not just on the image */}
                {hasMultiBatch && (
                  <span className="pg-card-badge-multi-batch">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      strokeLinejoin="round" aria-hidden="true">
                      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                      <polyline points="2 17 12 22 22 17"/>
                      <polyline points="2 12 12 17 22 12"/>
                    </svg>
                    {batchCount > 1 ? `${batchCount} Batches` : "Multi-batch"}
                  </span>
                )}

                {/* Out-of-stock but has inward records — can be oversold */}
                {isOutOfStockEligible && (
                  <span className="pg-card-badge-oos">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      strokeLinejoin="round" aria-hidden="true">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    No stock · Eligible to sell
                  </span>
                )}
              </div>
              </div>{/* /pg-card-clickable */}

              {/* Add to cart — inventory checks are handled by the backend */}
              <button
                type="button"
                className={`pg-card-add-btn${
                  status?.ok        ? " pg-card-add-btn--success"  :
                  status && !status.ok ? " pg-card-add-btn--error" : ""
                }`}
                disabled={isAdding || !gtin || !!status}
                onClick={() => !status && handleAdd(product)}
              >
                {isAdding ? (
                  <CircularProgress size={13} sx={{ color: "#fff" }} />
                ) : status?.ok ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Added to cart
                  </>
                ) : status && !status.ok ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 8v4"/><path d="M12 16h.01"/>
                    </svg>
                    {status.msg?.length > 28 ? status.msg.slice(0, 26) + "…" : (status.msg || "Failed")}
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                      strokeLinejoin="round" aria-hidden="true">
                      <path d="M12 5v14" /><path d="M5 12h14" />
                    </svg>
                    Add to cart
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>

    {/* Weighing item quantity modal */}
    {renderWeighingModal()}

    {/* Batch selection modal */}
    <BatchSelectionModal
      open={batchModalOpen}
      onClose={() => setBatchModalOpen(false)}
      mrps={batchMrps}
      disabled={batchDisabled}
      selectedProduct={batchProductName}
      selectedGtin={batchGtin}
      handlePrice={handlePrice}
      addItemWithScanner={addItemFromBatch}
      selectedMrp={selectedMrp}
      SelectedSellingPrice={selectedSP}
      batchNo={batchNo}
      purchasePrice={purchasePrice}
      sellingUnit={sellingUnitState}
      offerData={offerData}
      availableUnits={availableUnits}
      expiryDate={expiryDate}
      brandName=""
      packagingType=""
      variantName={batchProductName}
      vendorId=""
    />

    {/* Product detail drawer — opened from the info icon on each card */}
    {detailProduct && (
      <CartItemDrawer
        cartItemModalOpen={!!detailProduct}
        setCartItemModalOpen={(v) => { if (!v) setDetailProduct(null); }}
        cartItemData={detailProduct}
      />
    )}
    </>
  );
}
